import { WhiteLabelData } from "@web3auth/auth";
import { CHAIN_NAMESPACES, type WalletUiConfig } from "@web3auth/no-modal";
import { Action, getModule, Module, Mutation, VuexModule } from "vuex-module-decorators";

import config from "@/config";
import { PROJECT_ANALYTICS_EVENTS, projectAnalytics } from "@/helpers/analytics";
import { type CampaignForCli } from "@/helpers/attribution";
import ChainsListRegistry from "@/helpers/ChainsListRegistry";
import log from "@/helpers/log";
import { normalizeACLScopeValue } from "@/utils/acl";
import { constructChainLogoUrl, filterEligibleSmartAccountNetworks } from "@/utils/chains";
import {
  ETHEREUM_HOLESKY_CHAIN_ID,
  ETHEREUM_MAINNET_CHAIN_ID,
  ETHEREUM_SEPOLIA_CHAIN_ID,
  LINEA_MAINNET_CHAIN_ID,
  LINEA_SEPOLIA_TESTNET_CHAIN_ID,
  POPULAR_EVM_CHAINS,
  PROJECT_MODULE_KEY,
  SAPPHIRE_MAINNET,
  SocialLoginConfig,
} from "@/utils/enums";
import {
  type ACL,
  type ACLAccessType,
  type ACLCreateEntryResponse,
  type ACLCreateResponse,
  type ACLDetailResponse,
  type ACLEntry,
  type ACLEntryType,
  type ACLListResponse,
  type ChainsListRegistryResponse,
  type DBChainConfig,
  type DBChainConfigItem,
  DBSmartAccountsConfiguration,
  EIP_STANDARD,
  type EipStandard,
  type ExternalWalletsConfig,
  type LoginModalConfig,
  type Project,
  type ProjectDetailResponse,
  SmartAccountChainConfigItem,
  type UIChainConfig,
  type UIChainConfigItem,
} from "@/utils/interface";
import { constructInfuraUrl } from "@/utils/utils";
import store from "@/vuexStore";

import { authDelete, authGet, authPatch, authPost, authPut } from "../authStoreRequest";

export type CLITokenResult = {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
  campaign?: CampaignForCli;
};

export type ProjectUsersExportResponse = {
  export_id: string;
  status: string;
  total_rows: number;
};

export type ProjectUsersExportStatusResponse = {
  export_id: string;
  status: string;
  processed_rows: number;
  total_rows: number;
  download_url?: string;
  download_url_expires_at?: string;
  error?: string;
};

function normalizeACL(acl: ACL): ACL {
  return {
    ...acl,
    scope: normalizeACLScopeValue(acl.scope),
  };
}

function isAgenticProject(project: Project): boolean {
  return project.type === "agentic";
}
// @ts-expect-error decorator is picking the wrong overload as its type
@Module({
  namespaced: true,
  name: PROJECT_MODULE_KEY,
  store,
  dynamic: true,
})
class ProjectModule extends VuexModule {
  teamProjects: Record<number, Array<Project>> = {};

  archivedTeamProjects: Record<number, Array<Project> | undefined> = {};

  loginStas: Record<string, Record<string, number>> = {};

  mauStats: Record<string, Record<string, { verifier: string; mau: number }[]>> = {};

  chainsListFromRegistry: ChainsListRegistryResponse | null = null;

  public get projectsGetter() {
    return (teamId: number) => {
      const projects = this.teamProjects[teamId];
      return projects?.filter((project) => !isAgenticProject(project)) ?? [];
    };
  }

  public get agenticProjectsGetter() {
    return (teamId: number) => {
      const projects = this.teamProjects[teamId];
      return projects?.filter((project) => isAgenticProject(project)) ?? [];
    };
  }

  public get archivedProjectsGetter() {
    return (teamId: number) => {
      const projects = this.archivedTeamProjects[teamId];
      return projects?.filter((project) => !isAgenticProject(project));
    };
  }

  public get projectGetter(): (teamId: number, projectId: string) => Project | undefined {
    return (teamId, projectId) => {
      return (
        this.projectsGetter(teamId).find((project: Project) => project.project_id === projectId) ??
        this.archivedProjectsGetter(teamId)?.find((project: Project) => project.project_id === projectId)
      );
    };
  }

  public get agenticProjectGetter(): (teamId: number, projectId: string) => Project | undefined {
    return (teamId, projectId) => {
      return this.agenticProjectsGetter(teamId).find((project: Project) => project.project_id === projectId);
    };
  }

  public get loginStatsGetter() {
    return (projectId: string, from: string) => {
      return this.loginStas[projectId]?.[from];
    };
  }

  public get mauStatsGetter() {
    return (projectId: string, from: string) => {
      return this.mauStats[projectId]?.[from];
    };
  }

  public get projectsLoaded() {
    return (teamId: number) => {
      return Boolean(this.teamProjects[teamId]);
    };
  }

  public get projectChainConfig() {
    return (teamId: number, projectId: string): UIChainConfig => {
      const project = this.projectGetter(teamId, projectId);
      const chainConfigFromDB = JSON.parse(project?.chain_config ?? "[]") as DBChainConfig;
      const chainConfigFromDBMap = chainConfigFromDB.reduce((acc: Record<string, DBChainConfigItem>, dbChainConfigItem) => {
        acc[dbChainConfigItem.chainId] = dbChainConfigItem;
        return acc;
      }, {});

      const smartAccountsConfigurationFromDB = JSON.parse(project.smart_accounts_configuration || "{}") as DBSmartAccountsConfiguration;
      const smartAccountsChainConfigFromDB: DBSmartAccountsConfiguration["chains"] = smartAccountsConfigurationFromDB.chains
        ? smartAccountsConfigurationFromDB.chains
        : [];
      const smartAccountsChainConfigFromDBMap = smartAccountsChainConfigFromDB.reduce(
        (acc: Record<string, SmartAccountChainConfigItem>, chainConfig) => {
          acc[chainConfig.chainId] = chainConfig;
          return acc;
        },
        {}
      );

      const updatedChainConfigs = (this.chainsListFromRegistry ?? []).reduce((acc: Record<string, UIChainConfigItem>, chainsListRegistryItem) => {
        const { chainId, isInfuraSupported, isTestnet, chainNamespace } = chainsListRegistryItem;
        // networks are disabled by default
        const enabled = chainConfigFromDBMap[chainId]?.enabled ?? false;
        let rpcTarget = "";
        let wsTarget = "";
        if (isInfuraSupported) {
          rpcTarget = chainConfigFromDBMap[chainId]?.rpcTarget ?? constructInfuraUrl(chainId, projectId);
          wsTarget = chainConfigFromDBMap[chainId]?.wsTarget;
        }
        // required users to manually set rpcTarget for Sapphire Mainnet, Mainnet network projects
        // This is to prevent people using brittle default rpc targets that may go down and crash
        // their production app
        else if (project.network === SAPPHIRE_MAINNET && !isTestnet) {
          rpcTarget = chainConfigFromDBMap[chainId]?.rpcTarget;
          wsTarget = chainConfigFromDBMap[chainId]?.wsTarget;
        } else {
          rpcTarget = chainConfigFromDBMap[chainId]?.rpcTarget ?? chainsListRegistryItem.rpcTarget;
          wsTarget = chainConfigFromDBMap[chainId]?.wsTarget ?? chainsListRegistryItem.wsTarget;
        }

        let bundlerUrl = smartAccountsChainConfigFromDBMap[chainId]?.bundlerConfig?.url;
        if (isInfuraSupported) {
          bundlerUrl = bundlerUrl ?? constructInfuraUrl(chainId, projectId);
        }

        let sortScore = 0;
        // enabled > etherum > solana > infura supported >specific popular chains > others
        if (enabled) sortScore += 1000;
        if ([ETHEREUM_MAINNET_CHAIN_ID, ETHEREUM_SEPOLIA_CHAIN_ID, ETHEREUM_HOLESKY_CHAIN_ID].includes(chainId)) {
          sortScore += 400;
        } else if ([LINEA_MAINNET_CHAIN_ID, LINEA_SEPOLIA_TESTNET_CHAIN_ID].includes(chainId)) {
          sortScore += 300;
        } else if (chainNamespace === CHAIN_NAMESPACES.SOLANA) {
          sortScore += 200;
        }
        if (isInfuraSupported) sortScore += 50;
        if (POPULAR_EVM_CHAINS.includes(chainId)) sortScore += 10;

        acc[chainId] = {
          ...chainsListRegistryItem,
          rpcTarget,
          wsTarget,
          fallbackRpcTarget: chainConfigFromDBMap[chainId]?.fallbackRpcTargets?.[0],
          fallbackWsTarget: chainConfigFromDBMap[chainId]?.fallbackWsTargets?.[0],
          bundlerUrl,
          paymasterUrl: smartAccountsChainConfigFromDBMap[chainId]?.paymasterConfig?.url,
          enabled,
          logo: constructChainLogoUrl(chainsListRegistryItem),
          sortScore,
        };

        return acc;
      }, {});

      const customNetworks = chainConfigFromDB.reduce((acc: Record<string, UIChainConfigItem>, dbChainConfigItem) => {
        if (!dbChainConfigItem.isCustomNetwork) return acc;

        let sortScore = 20;

        // Use scores to sort chain config array by enabled, isInfuraSupported, displayName
        if (dbChainConfigItem.enabled) sortScore += 50;

        acc[dbChainConfigItem.chainId] = { ...dbChainConfigItem, sortScore };

        return acc;
      }, {});

      const chainConfigsArray = Object.values({ ...updatedChainConfigs, ...customNetworks });
      // sort chain config array by enabled, isInfuraSupported, displayName
      chainConfigsArray.sort((a, b) => {
        if (a.sortScore !== b.sortScore) return b.sortScore - a.sortScore;
        return a.displayName.localeCompare(b.displayName);
      });

      return chainConfigsArray;
    };
  }

  public get smartAccountNetworks() {
    return (teamId: number, projectId: string, eipStandard: EipStandard) => {
      return filterEligibleSmartAccountNetworks(this.projectChainConfig(teamId, projectId), eipStandard);
    };
  }

  public get smartAccountsEIP4337EnabledButBundlerNotConfiguredForAll() {
    return (teamId: number, projectId: string) => {
      const project = this.projectGetter(teamId, projectId);
      if (!project?.smart_accounts_enabled) return false;

      try {
        const config: DBSmartAccountsConfiguration = JSON.parse(project.smart_accounts_configuration || "{}");
        const resolvedEipStandard = config.eipStandard || EIP_STANDARD.EIP_4337;
        if (resolvedEipStandard !== EIP_STANDARD.EIP_4337) return false;
      } catch {
        return false;
      }

      return this.smartAccountNetworks(teamId, projectId, EIP_STANDARD.EIP_4337).some((chain) => !chain.bundlerUrl);
    };
  }

  @Action
  async fetchChainsListFromRegistry() {
    try {
      const chainsList = await ChainsListRegistry.fetchChains();
      this.setChainsListFromRegistry(chainsList);
    } catch (error) {
      log.error("Error fetching chains list from registry", error);
      throw error;
    }
  }

  @Mutation
  public setChainsListFromRegistry(chainsList: ChainsListRegistryResponse) {
    this.chainsListFromRegistry = chainsList;
  }

  @Mutation
  public setTeamProjects({ teamId, projects }: { teamId: number; projects: Array<Project> }) {
    this.teamProjects[teamId] = projects;
  }

  @Mutation
  public setArchivedTeamProjects({ teamId, projects }: { teamId: number; projects: Array<Project> }) {
    this.archivedTeamProjects[teamId] = projects;
  }

  @Mutation
  public removeTeamProject({ teamId, projectId }: { teamId: number; projectId: string }) {
    const projectIndex = this.teamProjects[teamId].findIndex((project) => project.project_id === projectId);
    this.teamProjects[teamId].splice(projectIndex, 1);
  }

  @Mutation
  public setLoginStats({ projectId, time, logins }: { projectId: string; time: string; logins: number }) {
    this.loginStas[projectId] = { ...this.loginStas[projectId], [time]: logins };
  }

  @Mutation
  public setMauStats({ projectId, time, stats }: { projectId: string; time: string; stats: { verifier: string; mau: number }[] }) {
    this.mauStats[projectId] = { ...this.mauStats[projectId], [time]: stats };
  }

  @Action
  async getProjects({ teamId }: { teamId: number }) {
    const query: Record<string, string> = { team_id: teamId.toString() };
    const response = await authGet(`${config.backendURL}/projects`, query, {});
    const { projects } = response as { projects: Array<Project> };
    this.setTeamProjects({ teamId, projects });
    return projects;
  }

  @Action
  async getArchivedProjects({ teamId }: { teamId: number }) {
    const query: Record<string, string> = { deleted_only: "true", team_id: teamId.toString() };
    const response = await authGet(`${config.backendURL}/projects`, query, {});
    const { projects } = response as { projects: Array<Project> };
    this.setArchivedTeamProjects({ teamId, projects });
    return projects;
  }

  @Action
  async ensureArchivedProjectsLoaded({ teamId }: { teamId: number }) {
    if (this.archivedTeamProjects[teamId] === undefined) {
      await this.getArchivedProjects({ teamId });
    }
  }

  @Action
  async refreshArchivedProjectsIfLoaded({ teamId }: { teamId: number }) {
    if (this.archivedTeamProjects[teamId] !== undefined) {
      await this.getArchivedProjects({ teamId });
    }
  }

  @Action
  async createProject({
    name,
    network,
    teamId,
    dappChainNamespaces,
  }: {
    name: string;
    network: string;
    teamId: number;
    dappChainNamespaces: string[];
  }): Promise<string> {
    try {
      const requestParameters: Record<string, unknown> = {
        name,
        network,
        dapp_chain_namespaces: dappChainNamespaces,
        team_id: teamId,
      };

      const response = await authPost(`${config.backendURL}/projects`, requestParameters, {});
      const { project_id: projectId } = response as { project_id: string };

      await this.getProjects({ teamId });
      return projectId;
    } catch (error) {
      log.error("Error creating project", error);
      throw error;
    }
  }

  /** Loads active team projects into the store, then returns agentic rows. */
  @Action
  async getAgenticProjects({ teamId }: { teamId: number }): Promise<Array<Project>> {
    await this.getProjects({ teamId });
    return this.agenticProjectsGetter(teamId);
  }

  // Posts to POST /projects/agentic-cli so the backend applies its agentic
  // defaults (type=agentic, payment-card bypass, SMS OTP off, key export off).
  // The normal POST /projects route intentionally does not apply these.
  @Action
  async createAgenticProject({
    name,
    network,
    teamId,
    dappChainNamespaces,
  }: {
    name: string;
    network: string;
    teamId: number;
    dappChainNamespaces: string[];
  }): Promise<string> {
    try {
      const requestParameters: Record<string, unknown> = {
        name,
        network,
        dapp_chain_namespaces: dappChainNamespaces,
        team_id: teamId,
      };

      const response = await authPost(`${config.backendURL}/projects/agentic-cli`, requestParameters, {});
      const { project_id: projectId } = response as { project_id: string };

      await this.getProjects({ teamId });
      return projectId;
    } catch (error) {
      log.error("Error creating agentic project", error);
      throw error;
    }
  }

  @Action
  async getCLIToken({ projectId }: { projectId: string }): Promise<CLITokenResult> {
    try {
      const response = await authPost(`${config.backendURL}/projects/${encodeURIComponent(projectId)}/cli-token`, {}, {});
      return response as CLITokenResult;
    } catch (error) {
      log.error("Error getting CLI token", error);
      throw error;
    }
  }

  @Action
  async getProjectDetail({ projectId, teamId }: { projectId: string; teamId: number }): Promise<ProjectDetailResponse> {
    try {
      const response = await authGet(`${config.backendURL}/projects/${projectId}`, { team_id: teamId.toString() }, {});
      return response as ProjectDetailResponse;
    } catch (error) {
      log.error("Error getting project detail", error);
      throw error;
    }
  }

  @Action
  async editProject({
    name,
    projectID,
    network,
    teamId,
    platformTypes,
    products,
  }: {
    name: string;
    projectID: string;
    network: string;
    teamId: number;
    platformTypes: Array<string>;
    products: Array<string>;
  }) {
    try {
      const requestParameters: Record<string, unknown> = {
        name,
        network,
        platform_types: platformTypes,
        products,
        team_id: teamId,
      };
      await authPatch(`${config.backendURL}/projects/${projectID}`, requestParameters, {});
      await this.getProjects({ teamId });
    } catch (error) {
      log.error("Error updating project", error);
      throw error;
    }
  }

  @Action
  async updateUserDataInIdToken({ projectId, teamId, userDataInIdToken }: { projectId: string; teamId: number; userDataInIdToken: boolean }) {
    const requestParameters: Record<string, unknown> = { user_data_in_id_token: userDataInIdToken, team_id: teamId };
    await authPatch(`${config.backendURL}/projects/${projectId}/user-data-in-id-token`, requestParameters, {});

    // track analytics
    projectAnalytics.track(PROJECT_ANALYTICS_EVENTS.PROJECT_USER_DATA_IN_ID_TOKEN_TOGGLED, {
      is_enabled: userDataInIdToken,
    });

    await this.getProjects({ teamId });
  }

  @Action
  async updateSessionDuration({ projectId, teamId, sessionDuration }: { projectId: string; teamId: number; sessionDuration: number }) {
    const requestParameters: Record<string, unknown> = { session_duration: sessionDuration, team_id: teamId };
    await authPatch(`${config.backendURL}/projects/${projectId}/session-duration`, requestParameters, {});

    // track analytics
    projectAnalytics.track(PROJECT_ANALYTICS_EVENTS.PROJECT_SESSION_DURATION_UPDATED, {
      session_time: sessionDuration,
    });

    await this.getProjects({ teamId });
  }

  @Action
  async archiveProject({ projectId, teamId }: { projectId: string; teamId: number }) {
    const requestParams: Record<string, string> = { team_id: teamId.toString() };
    try {
      await authDelete(`${config.backendURL}/projects/${projectId}`, requestParams, {});
      await this.getProjects({ teamId });
      await this.refreshArchivedProjectsIfLoaded({ teamId });
    } catch (error) {
      log.error("Error archiving project", error);
      throw error;
    }
  }

  @Action
  async restoreProject({ projectId, teamId }: { projectId: string; teamId: number }) {
    const queryParams: Record<string, number> = { team_id: teamId };
    await authPost(`${config.backendURL}/projects/${projectId}/restore`, queryParams, {});
    await this.getProjects({ teamId });
    await this.refreshArchivedProjectsIfLoaded({ teamId });
  }

  @Action
  async getProjectUsers({
    projectId,
    teamId,
    limit,
    cursor,
    userIdOrAddress,
    lastLoginFrom,
    lastLoginTo,
  }: {
    projectId: string;
    teamId: number;
    limit?: number;
    cursor?: string;
    userIdOrAddress?: string;
    lastLoginFrom?: string;
    lastLoginTo?: string;
  }) {
    const query: Record<string, string> = { team_id: teamId.toString() };
    if (limit != null) query.limit = limit.toString();
    if (cursor) query.cursor = cursor;
    if (userIdOrAddress) query.user_id_or_address = userIdOrAddress;
    if (lastLoginFrom) query.last_login_from = lastLoginFrom;
    if (lastLoginTo) query.last_login_to = lastLoginTo;
    const response = await authGet(`${config.backendURL}/projects/${projectId}/users`, query, {});
    return response;
  }

  @Action
  async exportProjectUsers({
    projectId,
    teamId,
    userIdOrAddress,
    lastLoginFrom,
    lastLoginTo,
  }: {
    projectId: string;
    teamId: number;
    userIdOrAddress?: string;
    lastLoginFrom?: string;
    lastLoginTo?: string;
  }): Promise<ProjectUsersExportResponse> {
    const data: Record<string, unknown> = { team_id: teamId };
    if (userIdOrAddress) data.user_id_or_address = userIdOrAddress;
    if (lastLoginFrom) data.last_login_from = lastLoginFrom;
    if (lastLoginTo) data.last_login_to = lastLoginTo;
    const response = await authPost(`${config.backendURL}/projects/${projectId}/users/export`, data, {});
    return response as ProjectUsersExportResponse;
  }

  @Action
  async getProjectUsersExportStatus({
    projectId,
    teamId,
    exportId,
  }: {
    projectId: string;
    teamId: number;
    exportId: string;
  }): Promise<ProjectUsersExportStatusResponse> {
    const query: Record<string, string> = { team_id: teamId.toString() };
    const response = await authGet(`${config.backendURL}/projects/${projectId}/users/export/${exportId}`, query, {});
    return response as ProjectUsersExportStatusResponse;
  }

  @Action
  async getWhitelists({ projectId, teamId }: { projectId: string; teamId: number }): Promise<string[]> {
    try {
      const queryParams: Record<string, string> = { team_id: teamId.toString() };

      const response = await authGet(`${config.backendURL}/projects/${projectId}/whitelists`, queryParams, {});
      const { urls } = response as { urls: string[] };
      return urls || [];
    } catch (error) {
      log.error("Error getting project whitelists", error);
      return [];
    }
  }

  @Action
  async addWhitelist({ url, projectId, teamId }: { url: string; projectId: string; teamId: number }) {
    const data: Record<string, string | number> = { url, team_id: teamId };
    try {
      await authPost(`${config.backendURL}/projects/${projectId}/whitelists`, data, {});

      // track analytics
      projectAnalytics.track(PROJECT_ANALYTICS_EVENTS.PROJECT_DOMAIN_SETTINGS_UPDATED, {
        operation_type: "add",
        whitelist_origin: url,
      });

      const projectIndex = this.teamProjects[teamId].findIndex((project) => project.project_id === projectId);
      if (projectIndex > -1) {
        const newProject = this.teamProjects[teamId][projectIndex];
        this.teamProjects[teamId].splice(projectIndex, 1, { ...newProject, whitelists: (newProject.whitelists ?? 0) + 1 });
      }
    } catch (error) {
      log.error("Error adding whitelist", error);
      throw error;
    }
  }

  @Action
  async deleteWhitelist({ url, projectId, teamId }: { url: string; projectId: string; teamId: number }) {
    const requestParams: Record<string, string> = { url, team_id: teamId.toString() };
    try {
      await authDelete(`${config.backendURL}/projects/${projectId}/whitelists`, requestParams, {});

      // track analytics
      projectAnalytics.track(PROJECT_ANALYTICS_EVENTS.PROJECT_DOMAIN_SETTINGS_UPDATED, {
        operation_type: "delete",
        whitelist_origin: url,
      });

      const projectIndex = this.teamProjects[teamId].findIndex((project) => project.project_id === projectId);
      if (projectIndex > -1) {
        const newProject = this.teamProjects[teamId][projectIndex];
        this.teamProjects[teamId].splice(projectIndex, 1, { ...newProject, whitelists: (newProject.whitelists ?? 0) - 1 });
      }
    } catch (error) {
      log.error("Error deleting whitelist", error);
      throw error;
    }
  }

  @Action
  async listACLs({ projectId }: { projectId: string }): Promise<ACL[]> {
    try {
      const response = await authGet(`${config.backendURL}/projects/${projectId}/acls`, {}, {});
      const { acls } = response as ACLListResponse;
      return (acls ?? []).map(normalizeACL);
    } catch (error) {
      log.error("Error getting project ACLs", error);
      throw error;
    }
  }

  @Action
  async getACL({ projectId, aclId }: { projectId: string; aclId: number }): Promise<ACL> {
    try {
      const response = await authGet(`${config.backendURL}/projects/${projectId}/acls/${aclId}`, {}, {});
      const { acl } = response as ACLDetailResponse;
      return normalizeACL(acl);
    } catch (error) {
      log.error("Error getting project ACL", error);
      throw error;
    }
  }

  @Action
  async createACL({
    projectId,
    name,
    accessType,
    scope,
  }: {
    projectId: string;
    name: string;
    accessType: ACLAccessType;
    scope?: string;
  }): Promise<ACL> {
    try {
      const body = {
        name,
        access_type: accessType,
        ...(scope ? { scope: normalizeACLScopeValue(scope) } : {}),
      };
      const response = await authPost(`${config.backendURL}/projects/${projectId}/acls`, body, {});
      const { acl } = response as ACLCreateResponse;
      return normalizeACL(acl);
    } catch (error) {
      log.error("Error creating project ACL", error);
      throw error;
    }
  }

  @Action
  async enableACL({ projectId, aclId }: { projectId: string; aclId: number }) {
    try {
      await authPut(`${config.backendURL}/projects/${projectId}/acls/${aclId}/enable`, {}, {});
    } catch (error) {
      log.error("Error enabling project ACL", error);
      throw error;
    }
  }

  @Action
  async disableACL({ projectId, aclId }: { projectId: string; aclId: number }) {
    try {
      await authPut(`${config.backendURL}/projects/${projectId}/acls/${aclId}/disable`, {}, {});
    } catch (error) {
      log.error("Error disabling project ACL", error);
      throw error;
    }
  }

  @Action
  async deleteACL({ projectId, aclId }: { projectId: string; aclId: number }) {
    try {
      await authDelete(`${config.backendURL}/projects/${projectId}/acls/${aclId}`, {}, {});
    } catch (error) {
      log.error("Error deleting project ACL", error);
      throw error;
    }
  }

  @Action
  async createACLEntry({
    projectId,
    aclId,
    entryType,
    value,
  }: {
    projectId: string;
    aclId: number;
    entryType: ACLEntryType;
    value: string;
  }): Promise<ACLEntry> {
    try {
      const response = await authPost(
        `${config.backendURL}/projects/${projectId}/acls/${aclId}/entries`,
        {
          entry_type: entryType,
          value,
        },
        {}
      );
      const { entry } = response as ACLCreateEntryResponse;
      return entry;
    } catch (error) {
      log.error("Error creating ACL entry", error);
      throw error;
    }
  }

  @Action
  async deleteACLEntry({ projectId, aclId, entryId }: { projectId: string; aclId: number; entryId: number }) {
    try {
      await authDelete(`${config.backendURL}/projects/${projectId}/acls/${aclId}/entries/${entryId}`, {}, {});
    } catch (error) {
      log.error("Error deleting ACL entry", error);
      throw error;
    }
  }

  @Action
  async enableTestAccounts({ projectId, teamId }: { projectId: string; teamId: number }) {
    const data: Record<string, number> = { team_id: teamId };
    await authPost(`${config.backendURL}/projects/${projectId}/test-account`, data, {});
    await this.getProjects({ teamId });
  }

  @Action
  async disableTestAccounts({ projectId, teamId }: { projectId: string; teamId: number }) {
    const queryParams: Record<string, string> = { team_id: teamId.toString() };
    await authDelete(`${config.backendURL}/projects/${projectId}/test-account`, queryParams, {});
    await this.getProjects({ teamId });
  }

  @Action
  async moveProjectToTeam({
    projectId,
    projectName,
    network,
    teamId,
    targetTeamId,
  }: {
    projectId: string;
    projectName: string;
    network: string;
    teamId: number;
    targetTeamId: number;
  }) {
    try {
      const data = { network, project_name: projectName, team_id: teamId, target_team_id: targetTeamId };
      await authPatch(`${config.backendURL}/projects/${projectId}/move`, data, {});
      this.removeTeamProject({ projectId, teamId });
    } catch (error) {
      log.error("Error moving project to team", error);
      throw error;
    }
  }

  @Action
  async renameAndMoveProjectToTeam({
    projectId,
    newProjectName,
    network,
    teamId,
    targetTeamId,
  }: {
    projectId: string;
    newProjectName: string;
    network: string;
    teamId: number;
    targetTeamId: number;
  }) {
    try {
      const data = { network, new_project_name: newProjectName, team_id: teamId, target_team_id: targetTeamId };
      await authPatch(`${config.backendURL}/projects/${projectId}/rename-and-move`, data, {});
      this.removeTeamProject({ projectId, teamId });
    } catch (error) {
      log.error("Error moving project to team", error);
      throw error;
    }
  }

  @Action
  async getProjectStats({
    projectId,
    teamId,
    day,
    month,
    cacheKey,
  }: {
    projectId: string;
    teamId: number;
    day?: string;
    month?: string;
    cacheKey: string;
  }) {
    try {
      const queryParams: Record<string, string> = { team_id: teamId.toString() };
      if (day) {
        queryParams.day = day;
      }
      if (month) {
        queryParams.month = month;
      }
      const response = await authGet(`${config.backendURL}/projects/${projectId}/login-stats`, queryParams, {});
      const { logins } = response as { logins: number };
      this.setLoginStats({ projectId, time: cacheKey, logins });
    } catch (error) {
      log.error("Error getting status of project", error);
      throw error;
    }
  }

  @Action
  async getMauStats({ projectId, teamId, from }: { projectId: string; teamId: number; from: string }) {
    try {
      const date = new Date(from);
      const fromMonth = String(date.getUTCMonth() + 1);
      const fromYear = String(date.getUTCFullYear());
      const queryParams = { fromMonth, fromYear, team_id: teamId.toString() };
      const response = await authGet(`${config.backendURL}/projects/${projectId}/mau-stats`, queryParams, {});
      const { stats } = response as { stats: { verifier: string; mau: number }[] };
      this.setMauStats({ projectId, time: from, stats });
    } catch (error) {
      log.error("Error getting status of project", error);
      throw error;
    }
  }

  @Action
  async setSmsOtp({ smsOtpEnabled, projectId, teamId }: { projectId: string; teamId: number; smsOtpEnabled: boolean }) {
    const data: Record<string, boolean | number> = { sms_otp_enabled: smsOtpEnabled, team_id: teamId };
    try {
      await authPost(`${config.backendURL}/projects/${projectId}/sms-otp`, data, {});
      await this.getProjects({ teamId });
    } catch (error) {
      log.error("Error setting SMS OTP", error);
      throw error;
    }
  }

  @Action
  async setEmailPasswordless({
    emailPasswordlessEnabled,
    projectId,
    teamId,
  }: {
    projectId: string;
    teamId: number;
    emailPasswordlessEnabled: boolean;
  }) {
    const data: Record<string, boolean | number> = { email_passwordless_enabled: emailPasswordlessEnabled, team_id: teamId };
    try {
      await authPost(`${config.backendURL}/projects/${projectId}/email-passwordless`, data, {});
      await this.getProjects({ teamId });
    } catch (error) {
      log.error("Error setting Email Passwordless", error);
      throw error;
    }
  }

  @Action
  async setExternalWallets({ externalWalletsEnabled, projectId, teamId }: { projectId: string; teamId: number; externalWalletsEnabled: boolean }) {
    try {
      const data: Record<string, boolean | number | string> = { team_id: teamId, external_wallets_enabled: externalWalletsEnabled };
      await authPost(`${config.backendURL}/projects/${projectId}/external-wallets`, data, {});

      // track analytics
      projectAnalytics.track(PROJECT_ANALYTICS_EVENTS.PROJECT_EXTERNAL_WALLETS_TOGGLED, {
        is_enabled: externalWalletsEnabled,
      });

      await this.getProjects({ teamId });
    } catch (error) {
      log.error("Error setting External Wallets", error);
      throw error;
    }
  }

  @Action
  async updateExternalWalletsConfig({
    projectId,
    teamId,
    externalWalletsConfig,
  }: {
    projectId: string;
    teamId: number;
    externalWalletsConfig: ExternalWalletsConfig;
  }) {
    const data = { external_wallets_config: JSON.stringify(externalWalletsConfig), team_id: teamId };
    try {
      await authPatch(`${config.backendURL}/projects/${projectId}/external-wallets-config`, data, {});

      // track analytics
      projectAnalytics.track(PROJECT_ANALYTICS_EVENTS.PROJECT_EXTERNAL_WALLETS_UPDATED, {
        disabled_wallets: externalWalletsConfig.disabledWallets,
        all_recommended_wallets_disabled: externalWalletsConfig.disableAllRecommendedWallets,
        all_other_wallets_disabled: externalWalletsConfig.disableAllOtherWallets,
      });

      await this.getProjects({ teamId });
    } catch (error) {
      log.error("Error updating external wallets config", error);
      throw error;
    }
  }

  @Action
  async setPasskeys({ passkeysEnabled, projectId, teamId }: { projectId: string; teamId: number; passkeysEnabled: boolean }) {
    const data: Record<string, boolean | number> = { passkeys_enabled: passkeysEnabled, team_id: teamId };
    try {
      await authPost(`${config.backendURL}/projects/${projectId}/passkeys`, data, {});
      await this.getProjects({ teamId });
    } catch (error) {
      log.error("Error setting Passkeys", error);
      throw error;
    }
  }

  @Action
  async setSocialLoginConfig({
    projectId,
    teamId,
    socialLoginConfig,
  }: {
    projectId: string;
    teamId: number;
    socialLoginConfig: Partial<SocialLoginConfig>;
  }) {
    const data = { social_login_config: JSON.stringify(socialLoginConfig), team_id: teamId };
    try {
      await authPatch(`${config.backendURL}/projects/${projectId}/social-login-config`, data, {});
      await this.getProjects({ teamId });
    } catch (error) {
      log.error("Error setting Social Login Config", error);
      throw error;
    }
  }

  @Action
  async updateEnableKeyExport({ enableKeyExport, projectId, teamId }: { projectId: string; teamId: number; enableKeyExport: boolean }) {
    const data: Record<string, boolean | number> = { key_export_enabled: enableKeyExport, team_id: teamId };
    try {
      await authPost(`${config.backendURL}/projects/${projectId}/key-export-enabled`, data, {});

      // track analytics
      projectAnalytics.track(PROJECT_ANALYTICS_EVENTS.PROJECT_KEY_EXPORT_TOGGLED, {
        is_enabled: enableKeyExport,
      });

      await this.getProjects({ teamId });
    } catch (error) {
      log.error("Error updating key export enabled", error);
      throw error;
    }
  }

  /**
   * @deprecated this action is not used anymore TODO: remove FE and BE
   */
  @Action
  async updateWalletConnect({
    walletConnectEnabled,
    walletConnectProjectID,
    projectId,
    teamId,
  }: {
    projectId: string;
    teamId: number;
    walletConnectEnabled?: boolean;
    walletConnectProjectID?: string;
  }) {
    const data: Record<string, boolean | number | string> = {
      team_id: teamId,
    };
    if (walletConnectEnabled !== undefined) data.wallet_connect_enabled = walletConnectEnabled;
    if (walletConnectProjectID !== undefined) data.wallet_connect_project_id = walletConnectProjectID;
    try {
      await authPost(`${config.backendURL}/projects/${projectId}/wallet-connect`, data, {});
      await this.getProjects({ teamId });
    } catch (error) {
      log.error("Error updating wallet connect", error);
      throw error;
    }
  }

  @Action
  async saveWhiteLabelConfiguration({
    projectId,
    whitelabelConfig,
    teamId,
  }: {
    projectId: string;
    whitelabelConfig: WhiteLabelData;
    teamId: number;
  }) {
    try {
      const response = await authPatch(
        `${config.backendURL}/projects/${projectId}/whitelabel`,
        { team_id: teamId, whitelabel_config: whitelabelConfig },
        {}
      );

      // track analytics
      projectAnalytics.track(PROJECT_ANALYTICS_EVENTS.PROJECT_BRANDING_UPDATED, {
        brand_logo_light_enabled: !!whitelabelConfig.logoLight,
        brand_logo_dark_enabled: !!whitelabelConfig.logoDark,
        logo_as_loader_enabled: whitelabelConfig.useLogoLoader,
        tnc_link_enabled: !!whitelabelConfig.tncLink,
        privacy_policy_enabled: !!whitelabelConfig.privacyPolicy,
        app_name: whitelabelConfig.appName,
        default_language: whitelabelConfig.defaultLanguage,
        theme: whitelabelConfig.mode,
        primary_color: whitelabelConfig.theme?.primary,
        on_primary_color: whitelabelConfig.theme?.onPrimary,
      });

      await this.getProjects({ teamId });
      return response;
    } catch (error) {
      log.error("Error saving white label configuration", error);
      throw error;
    }
  }

  @Action
  async saveLoginModalConfig({ projectId, loginModalConfig, teamId }: { projectId: string; loginModalConfig: LoginModalConfig; teamId: number }) {
    try {
      // TODO: make into authPut
      const response = await authPatch(
        `${config.backendURL}/projects/${projectId}/login-modal-config`,
        { team_id: teamId, login_modal_config: loginModalConfig },
        {}
      );

      // track analytics
      projectAnalytics.track(PROJECT_ANALYTICS_EVENTS.PROJECT_LOGIN_MODAL_UPDATED, {
        widget_type: loginModalConfig.widgetType,
        logo_alignment: loginModalConfig.logoAlignment,
        border_radius_type: loginModalConfig.borderRadiusType,
        button_radius_type: loginModalConfig.buttonRadiusType,
        sign_in_methods: loginModalConfig.signInMethods,
        previous_login_hint_enabled: loginModalConfig.addPreviousLoginHint,
        installed_external_wallets_enabled: loginModalConfig.displayInstalledExternalWallets,
        external_wallets_count_enabled: loginModalConfig.displayExternalWalletsCount,
      });

      await this.getProjects({ teamId });
      return response;
    } catch (error) {
      log.error("Error saving login modal configuration", error);
      throw error;
    }
  }

  @Action
  async saveWalletUiConfig({ projectId, teamId, walletUiConfig }: { projectId: string; teamId: number; walletUiConfig: WalletUiConfig }) {
    try {
      const data = { team_id: teamId, wallet_ui_config: walletUiConfig };
      const response = await authPatch(`${config.backendURL}/projects/${projectId}/wallet-ui-config`, data, {});

      // track analytics
      projectAnalytics.track(PROJECT_ANALYTICS_EVENTS.PROJECT_WALLET_UI_UPDATED, {
        portfolio_widget_enabled: walletUiConfig.enablePortfolioWidget,
        confirmation_modal_enabled: walletUiConfig.enableConfirmationModal,
        wallet_connect_enabled: walletUiConfig.enableWalletConnect,
        token_display_enabled: walletUiConfig.enableTokenDisplay,
        nft_display_enabled: walletUiConfig.enableNftDisplay,
        defi_positions_display_enabled: walletUiConfig.enableDefiPositionsDisplay,
        show_all_tokens_button_enabled: walletUiConfig.enableShowAllTokensButton,
        buy_button_enabled: walletUiConfig.enableBuyButton,
        send_button_enabled: walletUiConfig.enableSendButton,
        swap_button_enabled: walletUiConfig.enableSwapButton,
        receive_button_enabled: walletUiConfig.enableReceiveButton,
        portfolio_widget_position: walletUiConfig.portfolioWidgetPosition,
        default_portfolio: walletUiConfig.defaultPortfolio,
      });

      await this.getProjects({ teamId });
      return response;
    } catch (error) {
      log.error("Error saving wallet ui configuration", error);
      throw error;
    }
  }

  @Action
  async saveChainConfig({
    teamId,
    projectId,
    chainConfig,
    defaultChainId,
  }: {
    teamId: number;
    projectId: string;
    chainConfig: DBChainConfig;
    defaultChainId?: string | null;
  }) {
    const data = {
      team_id: teamId,
      chain_config: chainConfig,
      ...(defaultChainId !== undefined && { default_chain_id: defaultChainId ?? "" }),
    };
    await authPatch(`${config.backendURL}/projects/${projectId}/chain-config`, data, {});
    await this.getProjects({ teamId });
  }

  @Action
  async saveSmartAccountsConfiguration({
    teamId,
    projectId,
    smartAccountsConfiguration,
    smartAccountsEnabled,
  }: {
    teamId: number;
    projectId: string;
    smartAccountsConfiguration?: string;
    smartAccountsEnabled?: boolean;
  }) {
    const data = {
      team_id: teamId,
      ...(smartAccountsConfiguration !== undefined && { smart_accounts_configuration: smartAccountsConfiguration }),
      ...(smartAccountsEnabled !== undefined && { smart_accounts_enabled: smartAccountsEnabled }),
    };
    await authPatch(`${config.backendURL}/projects/${projectId}/smart-accounts-configuration`, data, {});
    await this.getProjects({ teamId });
  }

  @Action
  async resetWhitelabel({ projectId, teamId }: { projectId: string; teamId: number }) {
    await authPost(`${config.backendURL}/projects/${projectId}/whitelabel/reset`, { team_id: teamId }, {});
    await this.getProjects({ teamId });
  }

  @Action
  async resetSmartAccountsConfiguration({ projectId, teamId }: { projectId: string; teamId: number }) {
    await authPost(`${config.backendURL}/projects/${projectId}/smart-accounts-configuration/reset`, { team_id: teamId }, {});
    await this.getProjects({ teamId });
  }
}

const projectModule = getModule(ProjectModule);

export default projectModule;
